import { useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'

import { getInquiryOverlaps } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { GetInquiryOverlapsRequestSchema } from '@/gen/cortado/v1/inquiries_pb'
import { isValidUUID } from '@/lib/uuid'

/**
 * Hook to fetch overlapping leads and events for an inquiry
 *
 * This hook:
 * - Fetches inquiries and events that overlap with the given inquiry's date/time
 * - Uses a 2-hour buffer before and after the event
 * - Only considers active inquiry stages (New, InProgress, QuoteSent)
 * - Excludes the current inquiry and events from the same booking chain
 *
 * @param inquiryId - The inquiry ID to check for overlaps
 * @returns Query result with overlapping inquiries and events
 */
export function useInquiryOverlaps(inquiryId?: string) {
  const query = useQuery(
    getInquiryOverlaps,
    create(GetInquiryOverlapsRequestSchema, {
      inquiryId: inquiryId ?? '',
    }),
    {
      enabled: isValidUUID(inquiryId),
    },
  )

  const overlappingInquiries = query.data?.overlappingInquiries || []
  const overlappingEvents = query.data?.overlappingEvents || []

  return {
    overlappingInquiries,
    overlappingEvents,
    hasOverlaps: overlappingInquiries.length > 0 || overlappingEvents.length > 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasLoaded: query.isSuccess || !!query.data,
  }
}
